import type { ResolvedProject } from "@designs-by-maryam/types";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { type Config, type Data, DropZone } from "@measured/puck";
import { AboutLanding } from "@/components/home/about-landing";
import { CollaborationsCarousel } from "@/components/home/collaborations-carousel";
import { FeaturedProjects } from "@/components/home/featured-projects";
import { HeroSection } from "@/components/home/hero-section";
import { HomeContactSection } from "@/components/home/home-contact-section";
import { StatsSection } from "@/components/home/stats-section";
import { TestimonialsSection } from "@/components/home/testimonials-section";
import { ServicesSection } from "@/components/services/services-section";
import { orpc } from "@/utils/orpc";

/**
 * Puck Component Props Definitions
 * Each component type maps to its editable props
 * Note: Puck automatically injects `id` into all component props at runtime
 */
type HeroSectionProps = {
	/** Toggle to show/hide CRPEP badge - only layout control in Puck */
	showCRPEPBadge: boolean;
	/** Resolved hero content from site_settings.homepage (populated by resolveData) */
	resolvedHero?: {
		headline: string;
		subtitle: string;
		bio: string;
		photo: { url: string; alt: string } | null;
	};
	/** Resolved stats from site_settings.homepage (populated by resolveData) */
	resolvedStats?: Array<{ value: string; label: string }>;
};

// biome-ignore lint/complexity/noBannedTypes: Empty props for simple components
type StatsSectionProps = {};

/**
 * Project reference type for external field
 * Used to select specific projects via Puck editor
 */
type ProjectRef = {
	id: string;
	title: string;
	category: string;
};

type FeaturedProjectsProps = {
	/** Selected projects via external field (optional - uses auto-fetch if empty) */
	selectedProjects?: ProjectRef[];
	/** Resolved project data (populated by resolveData) */
	resolvedProjects?: ResolvedProject[];
};

// biome-ignore lint/complexity/noBannedTypes: Empty props for simple components
type CollaborationsCarouselProps = {};

// biome-ignore lint/complexity/noBannedTypes: Empty props for simple components
type ContactSectionProps = {};

// biome-ignore lint/complexity/noBannedTypes: Empty props for simple components
type AboutLandingProps = {};

type SpacerProps = {
	height: number;
};

/**
 * Section Wrapper Props
 * Wraps content with theme and spacing control
 */
type SectionWrapperProps = {
	theme: "default" | "alternate" | "dark";
	spacing: "compact" | "normal" | "hero";
	fullWidth: boolean;
};

/**
 * Services List Props
 * Dynamic services grid from CMS
 */
type ServicesListProps = {
	featured: boolean;
	/** Resolved services data (populated by resolveData) */
	resolvedServices?: Array<{
		id: string;
		title: string;
		shortTitle: string;
		description: string;
		iconName: string;
		features: string[];
		ctaText: string;
		ctaLink: string;
	}>;
};

type HeadingProps = {
	text: string;
	level: "h1" | "h2" | "h3" | "h4";
	align: "left" | "center" | "right";
};

type TextBlockProps = {
	content: string;
	align: "left" | "center" | "right";
};

/**
 * Testimonials Section Props
 * Displays client testimonials in a grid
 */
type TestimonialsSectionProps = {
	title: string;
	subtitle: string;
	maxItems: number;
};

/**
 * Combined props type for all components
 */
type ComponentProps = {
	HeroSection: HeroSectionProps;
	StatsSection: StatsSectionProps;
	FeaturedProjects: FeaturedProjectsProps;
	CollaborationsCarousel: CollaborationsCarouselProps;
	ContactSection: ContactSectionProps;
	AboutLanding: AboutLandingProps;
	ServicesList: ServicesListProps;
	TestimonialsSection: TestimonialsSectionProps;
	SectionWrapper: SectionWrapperProps;
	Spacer: SpacerProps;
	Heading: HeadingProps;
	TextBlock: TextBlockProps;
};

/**
 * Puck Configuration for the visual editor
 *
 * Defines the available components and their editable fields
 * for the homepage layout builder.
 */
export const puckConfig: Config<ComponentProps> = {
	categories: {
		sections: {
			title: "Page Sections",
			components: [
				"HeroSection",
				"StatsSection",
				"FeaturedProjects",
				"ServicesList",
				"TestimonialsSection",
				"CollaborationsCarousel",
				"ContactSection",
				"AboutLanding",
			],
		},
		layout: {
			title: "Layout",
			components: ["SectionWrapper", "Spacer", "Heading", "TextBlock"],
		},
	},
	components: {
		/**
		 * Hero Section Component
		 * Main homepage hero with photo, headline, and CTAs
		 *
		 * Content is fetched from site_settings.homepage via resolveData
		 * to maintain single source of truth (edit content in Payload CMS)
		 * Puck only controls layout options like showing/hiding the CRPEP badge
		 */
		HeroSection: {
			label: "Hero Banner",
			fields: {
				showCRPEPBadge: {
					type: "radio",
					label: "Display CRPEP License Badge",
					options: [
						{ label: "Show", value: true },
						{ label: "Hide", value: false },
					],
				},
			},
			defaultProps: {
				showCRPEPBadge: true,
			},
			/**
			 * resolveData: Fetch hero content from site_settings.homepage
			 *
			 * This keeps content in sync with CMS - editing in Payload
			 * automatically updates the Puck page without republishing
			 */
			resolveData: async ({ props }) => {
				try {
					const result = await orpc.getSiteSettings.call({ key: "homepage" });
					const homepage = result?.value as {
						hero?: {
							headline?: string;
							subtitle?: string;
							bio?: string;
							photo?: { url?: string; alt?: string } | null;
						};
						stats?: Array<{ value: string; label: string }>;
					} | null;

					return {
						props: {
							...props,
							resolvedHero: homepage?.hero
								? {
										headline:
											homepage.hero.headline ||
											"Crafting Exceptional Interiors",
										subtitle:
											homepage.hero.subtitle ||
											"Transforming Spaces with Elegant Interior Design",
										bio:
											homepage.hero.bio ||
											"I'm an interior designer passionate about creating spaces that reflect the warmth and sophistication of Bahraini culture.",
										photo: homepage.hero.photo?.url
											? {
													url: homepage.hero.photo.url,
													alt:
														homepage.hero.photo.alt ||
														"Interior Design Professional",
												}
											: null,
									}
								: undefined,
							resolvedStats: homepage?.stats,
						},
					};
				} catch {
					// Fallback - component will use its own defaults
					return { props };
				}
			},
			render: ({ showCRPEPBadge, resolvedHero, resolvedStats }) => (
				<HeroSection
					bio={resolvedHero?.bio}
					headline={resolvedHero?.headline}
					photoAlt={resolvedHero?.photo?.alt}
					photoSrc={resolvedHero?.photo?.url}
					showCRPEPBadge={showCRPEPBadge}
					stats={resolvedStats}
					subtitle={resolvedHero?.subtitle}
				/>
			),
		},

		/**
		 * Stats Section Component
		 * Displays animated statistics (projects, years, satisfaction)
		 */
		StatsSection: {
			label: "Statistics Counter",
			fields: {},
			defaultProps: {},
			render: () => <StatsSection />,
		},

		/**
		 * Featured Projects Component
		 * Project grid with category filters
		 *
		 * Supports two modes:
		 * 1. Auto-fetch: If no projects selected, fetches featured projects from API
		 * 2. Manual selection: If projects selected via external field, uses those
		 */
		FeaturedProjects: {
			label: "Featured Projects Grid",
			fields: {
				selectedProjects: {
					type: "external",
					placeholder:
						"Choose specific projects (leave empty to show all featured)...",
					showSearch: true,
					// biome-ignore lint/suspicious/noExplicitAny: Puck types expect any for getItemSummary
					getItemSummary: (item: any) => item?.title || "Unknown project",
					fetchList: async ({ query }: { query?: string }) => {
						// Fetch all published projects for selection
						const result = await orpc.listProjects.call({
							limit: 50,
							offset: 0,
						});
						const projects = result.data || [];

						// Filter by search query if provided
						if (query) {
							const searchLower = query.toLowerCase();
							return projects
								.filter(
									(p) =>
										p.title.toLowerCase().includes(searchLower) ||
										p.category.toLowerCase().includes(searchLower),
								)
								.map((p) => ({
									id: p.id,
									title: p.title,
									category: p.category,
								}));
						}

						return projects.map((p) => ({
							id: p.id,
							title: p.title,
							category: p.category,
						}));
					},
				},
			},
			defaultProps: {
				selectedProjects: [],
			},
			/**
			 * resolveData: Batch fetch project details to prevent N+1 queries
			 *
			 * This runs when:
			 * - Puck editor loads
			 * - Component is rendered via <Render>
			 * - resolveAllData() is called
			 */
			resolveData: async ({ props }, { changed }) => {
				// Skip if selectedProjects hasn't changed and we have resolved data
				if (!changed.selectedProjects && props.resolvedProjects) {
					return { props };
				}

				// If no projects selected, clear resolved data (component will auto-fetch)
				if (!props.selectedProjects || props.selectedProjects.length === 0) {
					return {
						props: {
							...props,
							resolvedProjects: [],
						},
					};
				}

				try {
					// Batch fetch all selected projects in single query
					const projectIds = props.selectedProjects.map(
						(p: ProjectRef) => p.id,
					);
					const projects = await orpc.getProjectsByIds.call({
						ids: projectIds,
					});

					return {
						props: {
							...props,
							resolvedProjects: projects.map((p) => ({
								id: p.id,
								title: p.title,
								category: p.category,
								year: p.year,
								images: p.images,
							})),
						},
					};
				} catch {
					// Don't crash - component will auto-fetch instead via useQuery
					return {
						props: {
							...props,
							resolvedProjects: [],
						},
					};
				}
			},
			render: ({ resolvedProjects }) => (
				<FeaturedProjects resolvedProjects={resolvedProjects} />
			),
		},

		/**
		 * Testimonials Section Component
		 * Displays client testimonials in a grid layout
		 */
		TestimonialsSection: {
			label: "Client Testimonials",
			fields: {
				title: {
					type: "text",
					label: "Section Title",
				},
				subtitle: {
					type: "text",
					label: "Section Subtitle",
				},
				maxItems: {
					type: "number",
					label: "Number of Testimonials to Show",
					min: 1,
					max: 9,
				},
			},
			defaultProps: {
				title: "What Our Clients Say",
				subtitle: "Trusted by homeowners and businesses across Bahrain",
				maxItems: 6,
			},
			render: ({ title, subtitle, maxItems }) => (
				<TestimonialsSection
					maxItems={maxItems}
					subtitle={subtitle}
					title={title}
				/>
			),
		},

		/**
		 * Collaborations Carousel Component
		 * Client logos carousel with testimonial quotes on hover
		 */
		CollaborationsCarousel: {
			label: "Client Logos Carousel",
			fields: {},
			defaultProps: {},
			render: () => <CollaborationsCarousel />,
		},

		/**
		 * Contact Section Component
		 * Contact call-to-action with form link
		 */
		ContactSection: {
			label: "Contact Call-to-Action",
			fields: {},
			defaultProps: {},
			render: () => <HomeContactSection />,
		},

		/**
		 * About Landing Component
		 * Brief about section for homepage
		 */
		AboutLanding: {
			label: "About Me Preview",
			fields: {},
			defaultProps: {},
			render: () => <AboutLanding />,
		},

		/**
		 * Spacer Component
		 * Adds vertical spacing between sections
		 */
		Spacer: {
			label: "Vertical Space",
			fields: {
				height: {
					type: "number",
					label: "Space Amount (pixels)",
					min: 0,
					max: 200,
				},
			},
			defaultProps: {
				height: 48,
			},
			render: ({ height }) => (
				<div aria-hidden="true" style={{ height: `${height}px` }} />
			),
		},

		/**
		 * Heading Component
		 * Configurable heading element
		 */
		Heading: {
			label: "Heading",
			fields: {
				text: {
					type: "text",
					label: "Text",
				},
				level: {
					type: "select",
					label: "Heading Level",
					options: [
						{ label: "H1", value: "h1" },
						{ label: "H2", value: "h2" },
						{ label: "H3", value: "h3" },
						{ label: "H4", value: "h4" },
					],
				},
				align: {
					type: "radio",
					label: "Alignment",
					options: [
						{ label: "Left", value: "left" },
						{ label: "Center", value: "center" },
						{ label: "Right", value: "right" },
					],
				},
			},
			defaultProps: {
				text: "Section Title",
				level: "h2",
				align: "center",
			},
			render: ({ text, level, align }) => {
				const Tag = level;
				const alignClasses = {
					center: "text-center",
					right: "text-right",
					left: "text-left",
				} as const;
				const sizeClasses = {
					h1: "text-4xl md:text-5xl",
					h2: "text-3xl md:text-4xl",
					h3: "text-2xl md:text-3xl",
					h4: "text-xl md:text-2xl",
				} as const;

				return (
					<Tag
						className={`container mx-auto px-4 font-bold tracking-tight ${sizeClasses[level]} ${alignClasses[align]}`}
					>
						{text}
					</Tag>
				);
			},
		},

		/**
		 * Text Block Component
		 * Paragraph text with alignment options
		 */
		TextBlock: {
			label: "Text Block",
			fields: {
				content: {
					type: "textarea",
					label: "Content",
				},
				align: {
					type: "radio",
					label: "Alignment",
					options: [
						{ label: "Left", value: "left" },
						{ label: "Center", value: "center" },
						{ label: "Right", value: "right" },
					],
				},
			},
			defaultProps: {
				content: "Add your content here...",
				align: "left",
			},
			render: ({ content, align }) => {
				const alignClasses = {
					center: "text-center",
					right: "text-right",
					left: "text-left",
				} as const;

				return (
					<div
						className={`container mx-auto px-4 text-muted-foreground ${alignClasses[align]}`}
					>
						<p className="mx-auto max-w-3xl text-lg leading-relaxed">
							{content}
						</p>
					</div>
				);
			},
		},

		/**
		 * Section Wrapper Component
		 * Container with theme and spacing controls for wrapping other components
		 */
		SectionWrapper: {
			label: "Section Wrapper",
			fields: {
				theme: {
					type: "select",
					label: "Theme",
					options: [
						{ label: "Default (Light)", value: "default" },
						{ label: "Alternate (Muted)", value: "alternate" },
						{ label: "Dark", value: "dark" },
					],
				},
				spacing: {
					type: "select",
					label: "Spacing",
					options: [
						{ label: "Compact", value: "compact" },
						{ label: "Normal", value: "normal" },
						{ label: "Hero (Large)", value: "hero" },
					],
				},
				fullWidth: {
					type: "radio",
					label: "Full Width",
					options: [
						{ label: "Yes", value: true },
						{ label: "No", value: false },
					],
				},
			},
			defaultProps: {
				theme: "default",
				spacing: "normal",
				fullWidth: false,
			},
			render: ({ theme, spacing, fullWidth }) => {
				const themeClasses = {
					default: "bg-background text-foreground",
					alternate:
						"bg-gradient-to-br from-muted/30 via-background to-muted/20 dark:from-muted/10 dark:via-background dark:to-muted/5",
					dark: "bg-neutral-950 text-neutral-100",
				} as const;

				const spacingClasses = {
					compact: "py-12",
					normal: "py-16 md:py-24",
					hero: "py-20 md:py-32",
				} as const;

				return (
					<section
						className={cn(
							themeClasses[theme],
							spacingClasses[spacing],
							"relative overflow-hidden",
						)}
					>
						<div className={fullWidth ? "w-full" : "container mx-auto px-4"}>
							<DropZone zone="section-content" />
						</div>
					</section>
				);
			},
		},

		/**
		 * Services List Component
		 * Dynamic services grid fetched from CMS with resolveData
		 */
		ServicesList: {
			label: "Services Showcase",
			fields: {
				featured: {
					type: "radio",
					label: "Which Services to Display",
					options: [
						{ label: "Featured Services Only", value: true },
						{ label: "All Services", value: false },
					],
				},
			},
			defaultProps: {
				featured: true,
			},
			/**
			 * resolveData: Fetch services from CMS with runtime validation
			 *
			 * This runs when:
			 * - Puck editor loads
			 * - Component is rendered via <Render>
			 * - resolveAllData() is called
			 */
			resolveData: async ({ props }, { changed }) => {
				// Skip if we already have resolved data and nothing changed
				if (!changed.featured && props.resolvedServices) {
					return { props };
				}

				try {
					// Fetch services from CMS via oRPC
					const result = await orpc.getServices.call({
						featured: props.featured,
					});

					// Runtime validation - ensure we have a valid array
					if (!(result?.data && Array.isArray(result.data))) {
						return {
							props: {
								...props,
								resolvedServices: [],
							},
						};
					}

					// Type the services from the API response
					type ServiceResult = (typeof result.data)[number];

					return {
						props: {
							...props,
							resolvedServices: result.data.map((svc: ServiceResult) => ({
								id: svc.id,
								title: svc.title,
								shortTitle: svc.short_title,
								description: svc.description,
								iconName: svc.icon_name,
								features: (svc.features as string[]) || [],
								ctaText: svc.cta_text,
								ctaLink: svc.cta_link,
							})),
						},
					};
				} catch {
					// Don't crash - component will show fallback
					return {
						props: {
							...props,
							resolvedServices: [],
						},
					};
				}
			},
			render: ({ resolvedServices }) => (
				<ServicesSection resolvedServices={resolvedServices} />
			),
		},
	},
};

/**
 * Type for Puck data structure
 */
export type PuckPageData = Data<ComponentProps>;

/**
 * Default homepage layout data
 *
 * Each content item requires a unique id for Puck's drag-and-drop
 */
export const defaultHomepageData = {
	content: [
		{
			type: "HeroSection" as const,
			props: {
				id: "hero-1",
				// Content is fetched from site_settings.homepage via resolveData
				// Only layout options are stored in Puck data
				showCRPEPBadge: true,
			},
		},
		{
			type: "CollaborationsCarousel" as const,
			props: { id: "logos-1", className: "" },
		},
		{
			type: "FeaturedProjects" as const,
			props: { id: "featured-1", className: "" },
		},
		{
			type: "ServicesList" as const,
			props: { id: "services-1", className: "", featured: true },
		},
		{
			type: "AboutLanding" as const,
			props: { id: "about-1", className: "" },
		},
		{
			type: "ContactSection" as const,
			props: { id: "contact-1", className: "" },
		},
	],
	root: {
		props: {},
	},
};
